import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import EnglishDatePicker from '../components/EnglishDatePicker/EnglishDatePicker';
import Sidebar from '../components/Sidebar';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://metro4.dcairo.com/api';

const Pours = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pours, setPours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    stations: [],
    specialSections: [],
    dateFrom: '',
    dateTo: ''
  });
  const [currentUser, setCurrentUser] = useState(null);
  
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // جلب بيانات المستخدم الحالي
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUser(response.data.data);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  // جلب جميع الصبات
  const fetchAllPours = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/pours`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPours(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching pours:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired, please login again');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPours();
  }, []);

  // فلترة الصبات
  const getFilteredPours = (status) => {
    return pours.filter(pour => {
      // فلتر الحالة
      if (status && pour.status !== status) return false;

      // فلتر المحطات
      if (filters.stations.length > 0) {
        const stationNames = filters.stations.map(id => {
          const stations = [
            { id: 'ST13', name: 'ST13 Giza' },
            { id: 'ST14', name: 'ST14 Giza Square' },
            { id: 'S15', name: 'S15 Alrawda' },
            { id: 'ST16', name: 'ST16 Almalek Elsaleh' },
            { id: 'ST17', name: 'ST17 Cut&Cover' }
          ];
          return stations.find(s => s.id === id)?.name;
        });
        if (!stationNames.includes(pour.station)) return false;
      }

      // فلتر الأقسام الخاصة
      if (filters.specialSections.length > 0) {
        const matchSpecial = filters.specialSections.some(sId => {
          if (sId === 'Chemical') return pour.chemistry > 0;
          if (sId === 'Grout') return pour.testType === 'Grout';
          if (sId === 'TM Trail') return pour.testType === 'T.M Trail';
          return false;
        });
        if (!matchSpecial) return false;
      }

      // فلتر التاريخ
      if (filters.dateFrom && filters.dateTo) {
        const pourDate = new Date(pour.castingDate);
        const fromDate = new Date(filters.dateFrom);
        const toDate = new Date(filters.dateTo);
        if (pourDate < fromDate || pourDate > toDate) return false;
      } else if (filters.dateFrom) {
        const pourDate = new Date(pour.castingDate);
        const fromDate = new Date(filters.dateFrom);
        if (pourDate < fromDate) return false;
      } else if (filters.dateTo) {
        const pourDate = new Date(pour.castingDate);
        const toDate = new Date(filters.dateTo);
        if (pourDate > toDate) return false;
      }

      return true;
    });
  };

  const handleFilterChange = (type, value) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (type === 'stations' || type === 'specialSections' || type === 'status') {
        if (newFilters[type].includes(value)) {
          newFilters[type] = newFilters[type].filter(v => v !== value);
        } else {
          newFilters[type] = [...newFilters[type], value];
        }
      } else {
        newFilters[type] = value;
      }
      return newFilters;
    });
  };

  const formatDateForAPI = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleClearFilters = () => {
    setFilters({
      status: [],
      stations: [],
      specialSections: [],
      dateFrom: null,
      dateTo: null
    });
  };

  const pendingCount = getFilteredPours('pending').length;
  const approvedCount = getFilteredPours('approved').length;
  const rejectedCount = getFilteredPours('rejected').length;

  return (
    <div className="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="dashboard-content">
        <div className="dashboard-header">
          <button className="menu-toggle" onClick={toggleSidebar}>
            ☰
          </button>
          <h1>📊 Pours Overview</h1>
        </div>

        <div className="dashboard-main">
          <style>{`
            .pours-container {
              padding: 20px;
              max-width: 100%;
              margin: 0 auto;
            }

            .filters-section {
              background: white;
              border-radius: 16px;
              padding: 25px;
              margin-bottom: 20px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.08);
            }

            .filters-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 20px;
            }

            .filters-title {
              font-size: 20px;
              font-weight: 700;
              color: #1e293b;
            }

            .toggle-filters-btn {
              background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 8px;
              cursor: pointer;
              font-weight: 600;
              transition: all 0.3s;
            }

            .toggle-filters-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
            }

            .filters-content {
              display: ${showFilters ? 'block' : 'none'};
              animation: fadeIn 0.3s;
            }

            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-10px); }
              to { opacity: 1; transform: translateY(0); }
            }

            .filter-group {
              margin-bottom: 20px;
            }

            .filter-label {
              font-size: 15px;
              font-weight: 600;
              color: #475569;
              margin-bottom: 10px;
              display: block;
            }

            .checkbox-group {
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
            }

            .checkbox-item {
              display: flex;
              align-items: center;
              gap: 8px;
              background: #f8fafc;
              padding: 8px 15px;
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s;
            }

            .checkbox-item:hover {
              background: #e2e8f0;
            }

            .checkbox-item input[type="checkbox"] {
              width: 18px;
              height: 18px;
              cursor: pointer;
            }

            .date-inputs {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
            }

            .date-input {
              padding: 10px 15px;
              border: 2px solid #e2e8f0;
              border-radius: 8px;
              font-size: 14px;
              transition: all 0.2s;
            }

            .date-input:focus {
              outline: none;
              border-color: #2563eb;
            }

            .filters-actions {
              display: flex;
              gap: 10px;
              margin-top: 20px;
            }

            .clear-btn {
              background: #f1f5f9;
              color: #475569;
              border: 2px solid #e2e8f0;
              padding: 12px 30px;
              border-radius: 8px;
              cursor: pointer;
              font-weight: 600;
              transition: all 0.3s;
            }

            .clear-btn:hover {
              background: #e2e8f0;
            }

            .cards-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 20px;
              margin-top: 20px;
            }

            .pour-card {
              background: white;
              border-radius: 16px;
              padding: 30px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.08);
              cursor: pointer;
              transition: all 0.3s;
              border: 3px solid transparent;
              position: relative;
              overflow: hidden;
            }

            .pour-card::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 6px;
              background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            }

            .pour-card:hover {
              transform: translateY(-5px);
              box-shadow: 0 8px 30px rgba(0,0,0,0.15);
              border-color: #f59e0b;
            }

            .pour-card.add-new::before {
              background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            }

            .pour-card.add-new:hover {
              border-color: #10b981;
            }

            .pour-card.pending::before {
              background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            }

            .pour-card.pending:hover {
              border-color: #f59e0b;
            }

            .pour-card.approved::before {
              background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            }

            .pour-card.approved:hover {
              border-color: #3b82f6;
            }

            .pour-card.temperature::before {
              background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            }

            .pour-card.temperature:hover {
              border-color: #ef4444;
            }

            .card-icon {
              font-size: 56px;
              margin-bottom: 15px;
              display: block;
            }

            .card-title {
              font-size: 22px;
              font-weight: 800;
              color: #1e293b;
              margin: 0 0 10px 0;
            }

            .card-description {
              font-size: 14px;
              color: #64748b;
              margin: 0 0 15px 0;
              line-height: 1.6;
            }

            .card-count {
              display: inline-block;
              background: #f1f5f9;
              padding: 8px 20px;
              border-radius: 20px;
              font-size: 16px;
              font-weight: 700;
              color: #1e293b;
            }

            .card-count.pending {
              background: #fef3c7;
              color: #92400e;
            }

            .card-count.approved {
              background: #dbeafe;
              color: #1e40af;
            }

            .empty-state {
              text-align: center;
              padding: 60px 20px;
              color: #94a3b8;
            }

            .empty-state-icon {
              font-size: 64px;
              margin-bottom: 20px;
              opacity: 0.5;
            }

            .empty-state-text {
              font-size: 18px;
              font-weight: 600;
              margin: 0;
            }

            @media (max-width: 768px) {
              .cards-grid {
                grid-template-columns: repeat(2, 1fr);
                gap: 12px;
              }

              .pour-card {
                padding: 20px;
              }

              .card-icon {
                font-size: 42px;
              }

              .card-title {
                font-size: 16px;
              }

              .card-description {
                font-size: 12px;
              }

              .card-count {
                font-size: 14px;
                padding: 6px 15px;
              }

              .date-inputs {
                grid-template-columns: 1fr;
              }
            }
          `}</style>

          <div className="pours-container">
            {/* Filters Section */}
            <div className="filters-section">
              <div className="filters-header">
                <h2 className="filters-title">🔍 Search & Filter</h2>
                <button 
                  className="toggle-filters-btn"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? '🔼 Hide Filters' : '🔽 Show Filters'}
                </button>
              </div>

              <div className="filters-content">
                {/* Status Filter */}
                <div className="filter-group">
                  <label className="filter-label">Status:</label>
                  <div className="checkbox-group">
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filters.status.includes('pending')}
                        onChange={() => handleFilterChange('status', 'pending')}
                      />
                      <span>Pending</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filters.status.includes('approved')}
                        onChange={() => handleFilterChange('status', 'approved')}
                      />
                      <span>Approved</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filters.status.includes('rejected')}
                        onChange={() => handleFilterChange('status', 'rejected')}
                      />
                      <span>Rejected</span>
                    </label>
                  </div>
                </div>

                {/* Stations Filter */}
                <div className="filter-group">
                  <label className="filter-label">Stations:</label>
                  <div className="checkbox-group">
                    {['ST13', 'ST14', 'S15', 'ST16', 'ST17'].map(station => (
                      <label key={station} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={filters.stations.includes(station)}
                          onChange={() => handleFilterChange('stations', station)}
                        />
                        <span>{station}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Special Sections Filter */}
                <div className="filter-group">
                  <label className="filter-label">Special Sections:</label>
                  <div className="checkbox-group">
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filters.specialSections.includes('Chemical')}
                        onChange={() => handleFilterChange('specialSections', 'Chemical')}
                      />
                      <span>Chemical Test</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filters.specialSections.includes('Grout')}
                        onChange={() => handleFilterChange('specialSections', 'Grout')}
                      />
                      <span>Grout</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filters.specialSections.includes('TM Trail')}
                        onChange={() => handleFilterChange('specialSections', 'TM Trail')}
                      />
                      <span>T.M Trail</span>
                    </label>
                  </div>
                </div>

                {/* Date Range Filter */}
                <div className="filter-group">
                  <label className="filter-label">Date Range:</label>
                  <div className="date-inputs">
                    <EnglishDatePicker
                      selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
                      onChange={(date) => handleFilterChange('dateFrom', formatDateForAPI(date))}
                      maxDate={filters.dateTo ? new Date(filters.dateTo) : new Date()}
                      placeholderText="From Date"
                    />
                    <EnglishDatePicker
                      selected={filters.dateTo ? new Date(filters.dateTo) : null}
                      onChange={(date) => handleFilterChange('dateTo', formatDateForAPI(date))}
                      minDate={filters.dateFrom ? new Date(filters.dateFrom) : null}
                      maxDate={new Date()}
                      placeholderText="To Date"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="filters-actions">
                  <button className="clear-btn" onClick={handleClearFilters}>
                    🗑️ Clear Filters
                  </button>
                </div>
              </div>
            </div>
			{/* Cards Grid */}
            {loading ? (
              <div className="empty-state">
                <div className="empty-state-icon">⏳</div>
                <p className="empty-state-text">Loading...</p>
              </div>
            ) : (
              <div className="cards-grid">
                {/* Add New Pour Card */}
                <div 
                  className="pour-card add-new"
                  onClick={() => navigate('/pours/flow-up')}
                >
                  <span className="card-icon">📝</span>
                  <h3 className="card-title">إضافة صبة جديدة</h3>
                  <p className="card-description">
                    تسجيل صبة جديدة بجميع البيانات والمعلومات المطلوبة
                  </p>
                </div>

                {/* Pending Pours Card */}
                <div 
                  className="pour-card pending"
                  onClick={() => navigate('/pours/pending')}
                >
                  <span className="card-icon">⏳</span>
                  <h3 className="card-title">سجل الصبات - تحت المراجعة</h3>
                  <p className="card-description">
                    الصبات في انتظار موافقة المهندس أو المشرف
                  </p>
                  <span className="card-count pending">{pendingCount} Pour</span>
                </div>

                {/* Approved Pours Card */}
                <div 
                  className="pour-card approved"
                  onClick={() => navigate('/pours/approved')}
                >
                  <span className="card-icon">✅</span>
                  <h3 className="card-title">الصبات المعتمدة</h3>
                  <p className="card-description">
                    جميع الصبات المعتمدة رسمياً
                  </p>
                  <span className="card-count approved">{approvedCount} Pour</span>
                </div>

                {/* Temperature Tracking Card */}
                <div 
                  className="pour-card temperature"
                  onClick={() => navigate('/pours/temperature')}
                >
                  <span className="card-icon">🌡️</span>
                  <h3 className="card-title">متابعة حرارة الصبات</h3>
                  <p className="card-description">
                    مراقبة وتسجيل درجات الحرارة للصبات النشطة
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pours;